import { Quaternion, Vector3 } from "@babylonjs/core/Maths/math.vector";
import { angleBetweenVectors3, quaternionFromAxes, VECTOR3_X } from "../../core/utils/MathUtils";
import { CameraMode } from "../../core/view/CameraControl";
import { degToRad, PI_DIV2 } from "../../utils/math";
import { Interactor as CoreInteractor, InteractorType } from "./../../core/tool/Interactor";
import * as basket from "./../../eaiws/basket";
import { executeActions } from "./Actions";
import { DimChangeMode } from "./DimChangeMode";
import { getSpriteUrl } from "./InteractorSprites";
export class Interactor extends CoreInteractor {
    get oapInteractor() {
        return (this.mOapInteractor);
    }
    get interactors() {
        return (this.mInteractors);
    }
    get cachedBoundingClientRect() {
        return (this.mClientRect);
    }
    get worldPosition() {
        return (this.mWorldPosition);
    }
    get hiddenMode() {
        return (this.mHiddenMode);
    }
    constructor(pInteractors, pOapInteractor, pOapSymbol) {
        super(document.createElement("div"), pInteractors.manager.app.viewer);
        this.onUpdateViewPosition = (pView) => {
            if (!this.mUpdateVisibility)
                return;
            if (this.mViewAngle != null && this.mWorldDirection != null) {
                const tCameraVec = Interactor._GetDirectionToCamera(this.mWorldPosition, pView);
                const tAngle = angleBetweenVectors3(this.mWorldDirection, tCameraVec);
                this.mCurrentAngleVisibility = tAngle <= this.mViewAngle;
                if (!this.mCurrentAngleVisibility) {
                    this.visible = false;
                    return; //no need for further checks
                }
                //only set interactor visible if hidden mode is not used, otherwise wait for requestVisibilityCheck(), see setHidden()
                if (!this.mHiddenMode) {
                    this.visible = this.mCurrentAngleVisibility;
                }
            }
            else {
                this.mCurrentAngleVisibility = true;
            }
            if (this.mHiddenMode) {
                this.mInteractors._requestVisibilityCheck(this);
            }
            else {
                if (this.mDimChangeAction != null) {
                    this.visible = DimChangeMode.MayStartDimChangeMode(this.mInteractors.article, this.mView, this.mDimChangeAction.dimChanges);
                }
                else {
                    this.visible = true;
                }
            }
        };
        this.mInteractors = pInteractors;
        this.mOapInteractor = pOapInteractor;
        this.mClientRect = undefined;
        this.mHiddenMode = false;
        this.mUpdateVisibility = true;
        this.mCurrentAngleVisibility = true;
        this.mDimChangeAction = null;
        const tSpan = document.createElement("span");
        tSpan.style.backgroundImage = "url('" + getSpriteUrl() + "')";
        this.mElement.appendChild(tSpan);
        this.mElement.className = "wcf-cf " + this.getInteractorCssClass(this.mOapInteractor);
        //action
        if (pOapInteractor.directActions != null && pOapInteractor.directActions.length > 0) {
            const tAction = pOapInteractor.directActions[0].actionData;
            if (tAction != null && (tAction instanceof basket.OAPDimChangeAction || tAction instanceof basket.OAPDimChange2Action)) {
                this.mDimChangeAction = tAction;
            }
        }
        this.type = InteractorType.Button;
        this.onTriggerAction = () => {
            var _a;
            this.mClientRect = this.mElement.getBoundingClientRect();
            if (this.mDimChangeAction != null) {
                const tContext = DimChangeMode.GetContext(this);
                const tTool = new DimChangeMode(this.mInteractors.manager.app, tContext, (_a = this.mDimChangeAction.dimChanges) !== null && _a !== void 0 ? _a : []);
                this.mInteractors.manager.app.tools.startTool(tTool);
                return;
            }
            if (pOapInteractor.actionIds != null) {
                void executeActions(pInteractors.manager, pInteractors.article, pOapInteractor.actionIds, this);
            }
        };
        //position
        this.customCssTransform = "translate(-50%, -50%)";
        this.worldMatrix = pInteractors.article.worldTransform;
        if (pOapSymbol != null) {
            if (pOapSymbol.symbolOffset != null) {
                this.position.set(pOapSymbol.symbolOffset.x, pOapSymbol.symbolOffset.y, -pOapSymbol.symbolOffset.z);
            }
        }
        else {
            //fallback for backwards compatibility
            if (pOapInteractor.symbolOffset != null) {
                this.position.set(pOapInteractor.symbolOffset.x, pOapInteractor.symbolOffset.y, -pOapInteractor.symbolOffset.z);
            }
        }
        this.mWorldPosition = this.position.clone();
        Vector3.TransformCoordinatesToRef(this.mWorldPosition, this.worldMatrix, this.mWorldPosition);
        //setup oap interactor visibility
        if (pOapSymbol != null) {
            if (pOapSymbol.viewAngle != null) {
                this.mViewAngle = degToRad(pOapSymbol.viewAngle * 0.5);
            }
            if (pOapSymbol.direction != null) {
                this.mWorldDirection = new Vector3(pOapSymbol.direction.x, pOapSymbol.direction.y, -pOapSymbol.direction.z).normalize();
                Vector3.TransformNormalToRef(this.mWorldDirection, this.worldMatrix, this.mWorldDirection);
                //3d interactor
                if (pOapSymbol.orientationX != null) {
                    const tWorldOrientationX = new Vector3(pOapSymbol.orientationX.x, pOapSymbol.orientationX.y, -pOapSymbol.orientationX.z).normalize();
                    Vector3.TransformNormalToRef(tWorldOrientationX, this.worldMatrix, tWorldOrientationX);
                    const tAxisZ = Vector3.Cross(tWorldOrientationX, this.mWorldDirection);
                    this.transform3dRotation = quaternionFromAxes(tWorldOrientationX, this.mWorldDirection, tAxisZ);
                    //rotate 90° around x axis
                    this.transform3dRotation.multiplyInPlace(Quaternion.RotationAxis(VECTOR3_X, PI_DIV2));
                    this.transform3d = true;
                }
            }
            if (pOapSymbol.hiddenMode != null) {
                this.mHiddenMode = pOapSymbol.hiddenMode;
                //visibility in hiddenmode is evaluated in a timeout, to prevent flickering we initially hide the interactor
                if (this.mHiddenMode) {
                    this.visible = false;
                }
            }
        }
        this.onBeforeUpdateViewPosition = this.onUpdateViewPosition;
    }
    /**
     * used from `Interactors.checkInteractorVisibility()`
     */
    setHidden(pHidden) {
        /*
            * setting the visibility triggers, onUpdateViewPosition
            * we do not want to trigger the visibility check again here
            */
        this.mUpdateVisibility = false;
        let tVisible = !pHidden && this.mCurrentAngleVisibility;
        if (tVisible && this.mDimChangeAction != null) {
            tVisible = DimChangeMode.MayStartDimChangeMode(this.mInteractors.article, this.mView, this.mDimChangeAction.dimChanges);
        }
        this.visible = tVisible;
        this.mUpdateVisibility = true;
    }
    //// internal //////////////////////////////////////////////////////////////////////////////
    /** @internal */
    static _GetDirectionToCamera(pPos, pView) {
        let tCameraVec;
        if (pView.cameraControl.cameraMode === CameraMode.Perspective) {
            tCameraVec = pView.cameraControl.position.subtract(pPos);
            tCameraVec.normalize();
        }
        else
            tCameraVec = pView.cameraControl.direction.negate();
        return (tCameraVec);
    }
    getInteractorCssClass(pOapInteractor) {
        let tName = Interactor.INTERACTOR_NAME_MAP.get(pOapInteractor.symbolType);
        if (tName == null) {
            tName = "add";
        }
        let tInteractorClass = "oap-interactor-";
        let tSymbolClass = "oap-symbol-";
        let tSize = pOapInteractor.symbolSize;
        if (tSize === "Small" && Interactor.INTERACTOR_NO_SMALL.has(tName)) {
            tSize = "Medium";
        }
        switch (tSize) {
            case ("Small"):
                tInteractorClass += "s";
                tSymbolClass += "s-";
                break;
            case ("Medium"):
                tInteractorClass += "m";
                tSymbolClass += "m-";
                break;
            default:
                tInteractorClass += "l";
                tSymbolClass += "l-";
                break;
        }
        tSymbolClass += tName;
        return (tInteractorClass + " " + tSymbolClass);
    }
}
//// private ///////////////////////////////////////////////////////////////////////////////
Interactor.INTERACTOR_NAME_MAP = new Map([
    ["Add", "add"],
    ["Delete", "delete"],
    ["Edit", "edit"],
    ["Flip", "flip"],
    ["RotateNY90", "rotate-ny90"],
    ["RotatePY90", "rotate-py90"],
    ["Material", "material"],
    ["PosUp", "pos-up"],
    ["PosDown", "pos-down"],
    ["Pos2Left", "pos-2-left"],
    ["Pos2Right", "pos-2-right"],
    ["PosVertical", "pos-vertical"],
    ["PosHorizontal", "pos-horizontal"],
    ["ChangeDimUp", "change-dim-up"],
    ["ChangeDimDown", "change-dim-down"],
    ["ChangeDim2Left", "change-dim-2-left"],
    ["ChangeDim2Right", "change-dim-2-right"],
    ["ChangeDimVertical", "change-dim-vertical"],
    ["ChangeDimHorizontal", "change-dim-horizontal"],
    ["OnOff", "on-off"],
    ["Video", "video"],
    ["StartDimChange", "start-dim-change"],
    //["...", "finish"], //not an OAP symbol, used from dim change tool
    ["Attention", "attention"],
    //["...", "information"], //not part of the OAP spec yet but contained in the sprite
    ["RotateNY", "rotate-ny"],
    ["RotatePY", "rotate-py"],
    ["Electrification", "electrification"],
    ["Lighting", "lighting"],
    //backwards compatibility
    ["DeleteObj", "delete"],
    ["PropChange", "edit"],
    ["PropEdit", "edit"],
    ["RotateX180", "flip"],
    ["RotateY180", "flip"],
    ["RotateZ180", "flip"]
]);
Interactor.INTERACTOR_NO_SMALL = new Set(["flip", "rotate-ny90", "rotate-py90"]);
